/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Breadcrumb } from "antd";
import moment from "moment";
import { db_user } from "../../../components/firestore_user";
import {
  getYoutubeChannelBinding,
  getYoutubeDailyEstimates,
} from "../../../api/youtubeEstimates";
import YoutubeAnalyticsView from "./YoutubeAnalyticsView";
import { dateBoundsForCalendarMonth } from "./mockData";
import {
  applyYoutubeLicenseSignatureToProject,
  useYoutubeLicenseContract,
} from "./useYoutubeLicenseContract";
import { useIsAdmin } from "../../../hooks/useIsAdmin";

const EMPTY_LIVE = "No data for this month.";

export default function YoutubeAnalyticsContainer({ auth_user }) {
  const { id } = useParams();
  const uid = auth_user?.user?.uid;

  const [project, setProject] = useState({});
  const [loadingProject, setLoadingProject] = useState(true);
  const [estimates, setEstimates] = useState([]);
  const [binding, setBinding] = useState(null);
  const [bindingLoaded, setBindingLoaded] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() =>
    moment().startOf("month")
  );

  const loadProject = useCallback(async () => {
    if (!id || !uid) return;
    setLoadingProject(true);
    try {
      const doc = await db_user.collection("projects").doc(id).get();
      setProject(doc.exists ? doc.data() : {});
    } catch (e) {
      console.error(e);
      setProject({});
    } finally {
      setLoadingProject(false);
    }
  }, [id, uid]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setBindingLoaded(false);
    getYoutubeChannelBinding(id)
      .then((res) => {
        if (!cancelled) {
          setBinding(res.binding || null);
          setBindingLoaded(true);
        }
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          setBinding(null);
          setBindingLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const hasYoutube = bindingLoaded && binding != null;
  const { loaded: adminLoaded, isAdmin } = useIsAdmin(auth_user);
  const bypassContractGate = adminLoaded && isAdmin;
  const {
    contractConfig,
    contractSigned,
    contractRequired,
    contractGateActive,
    canViewPage,
    contractSigning,
    handleContractSign,
  } = useYoutubeLicenseContract({
    projectId: id,
    project,
    uid,
    hasYoutube,
    bypassContractGate,
  });

  const loadEstimates = useCallback(async () => {
    if (!id || !canViewPage) return;
    const bounds = dateBoundsForCalendarMonth(selectedMonth);
    if (!bounds) return;
    const [fromM, toM] = bounds;
    setLoadingData(true);
    setError(null);
    try {
      const estRes = await getYoutubeDailyEstimates(id, {
        from: fromM.format("YYYY-MM-DD"),
        to: toM.format("YYYY-MM-DD"),
      });
      if (estRes.error) setError(estRes.error);
      setEstimates(estRes.estimates || []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoadingData(false);
    }
  }, [id, selectedMonth, canViewPage]);

  const isLive = project.distributionStatus === "live";

  useEffect(() => {
    if (!loadingProject && isLive && canViewPage) {
      loadEstimates();
    }
  }, [loadingProject, isLive, canViewPage, loadEstimates]);

  const onContractSign = useCallback(
    async (signerName) => {
      const name = await handleContractSign(signerName);
      if (name) {
        setProject((prev) =>
          applyYoutubeLicenseSignatureToProject(prev, uid, name)
        );
      }
    },
    [handleContractSign, uid]
  );

  const breadcrumbSlot = (
    <>
      <Breadcrumb.Item>
        <Link to="/">My Projects</Link>
      </Breadcrumb.Item>
      <Breadcrumb.Item>
        <Link to={`/project/${id}`}>{project.title || id}</Link>
      </Breadcrumb.Item>
      <Breadcrumb.Item>YouTube revenue (estimates)</Breadcrumb.Item>
    </>
  );

  const handleSelectedMonthChange = (m) => {
    if (m && m.isValid()) setSelectedMonth(m.clone().startOf("month"));
  };

  return (
    <YoutubeAnalyticsView
      loadingProject={loadingProject}
      bindingLoaded={bindingLoaded}
      isLive={isLive}
      breadcrumbSlot={breadcrumbSlot}
      backHref={`/project/${id}`}
      noticeSlot={null}
      binding={binding}
      chartAreaLoading={loadingData}
      error={error}
      selectedMonth={selectedMonth}
      onSelectedMonthChange={handleSelectedMonthChange}
      estimates={estimates}
      emptyEstimatesDescription={EMPTY_LIVE}
      contractConfig={contractConfig}
      contractRequired={contractRequired}
      contractSigned={contractSigned}
      contractGateActive={contractGateActive}
      canViewPage={canViewPage}
      bypassContractGate={bypassContractGate}
      contractSigning={contractSigning}
      onContractSign={onContractSign}
    />
  );
}
